import {
  fetchCustomersV2,
  getCustomers,
  getCustomerMetadata,
  useBranches,
  useLedgerSyncStatus,
  uploadErpFile,
} from "@api";
import { useCustomerGroups } from "@context/CustomerGroupsContext";
import { usePaymentProfilesContext } from "@context/PaymentProfilesContext";
import { usePaymentRequestTemplatesContext } from "@context/PaymentRequestTemplatesContext";
import {
  useShowFileImport,
  useRequestTemplates,
  useAiFeatures,
  useSmartNoteV2,
  useHomeV2,
} from "@flags";
import { updateCustomer } from "@auth";
import { ServerTable, PageHeader, PageTitle } from "@components";
import { toast } from "@components/toast/Toast";
import { useTableFilters } from "@components/tables/shared";
import { useTableQuery } from "@hooks";
import { useQueryClient } from "@tanstack/react-query";
import { formatAmount, formatTimeAgo, getMonth, formatDateTime } from "@utils";
import { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { CreditCardIcon } from "@heroicons/react/24/outline";
import { useNavigate, useOutletContext, useParams, useSearchParams } from "react-router-dom";
import TradeAccountDetails from "../displays/TradeAccountDetails";
import BatchRequestDrawer from "../flows/BatchRequestDrawer";
import BatchTemplateRequestModal from "../flows/BatchTemplateRequestModal";
import SingleRequestDrawer from "../flows/SingleRequestDrawer";
import { buildApiQuery } from "@utils";
import { useMerchant } from "@auth";
import GroupLabels from "../components/GroupLabels";
import WorkingSetBar from "../components/WorkingSetBar";
import WorkingSetTitle from "../components/WorkingSetTitle";
import WorkingSetRead, { useWorkingSetReadCollapsed, WorkingSetReadChip } from "../components/WorkingSetRead";
import WorkingSetBuilderModal from "../../tasks/components/WorkingSetBuilderModal";
import RemoveWorkingSetDialog from "../../tasks/components/RemoveWorkingSetDialog";
import { useIsBranchScoped } from "../branchScope";
import { useWorkingSetList, withWorkingSet } from "../../tasks/hooks/workingSets";
import {
  PayerTypeLabel,
  WatchFlagChips,
  DaysPastDueCell,
  PerformanceScoreCell,
  PAYER_TYPE_TOOLTIP,
  DAYS_PAST_DUE_TOOLTIP,
  WATCH_FLAGS_TOOLTIP,
  PAYMENT_SCORE_TOOLTIP,
  PAYER_TYPE_OPTIONS,
  WATCH_FLAG_OPTIONS,
} from "../components/FactCells";
import PaymentProfileSelector from "../components/PaymentProfileSelector";
import ChangePaymentProfileModal from "../components/ChangePaymentProfileModal";
import { PaperAirplaneIcon } from "@heroicons/react/24/outline";

const Accounts = () => {
  const { merchant } = useMerchant();
  const isBranchScoped = useIsBranchScoped();
  const navigate = useNavigate();
  const { groupId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    groups: customerGroups,
    loading: groupsLoading,
    getGroupName,
  } = useCustomerGroups();
  const queryClient = useQueryClient();
  const [customerId, setCustomerId] = useState(null);
  const [batchCustomerRequestData, setBatchCustomerRequestData] =
    useState(null);
  const [templateBatchData, setTemplateBatchData] = useState(null);
  const [singleCustomerRequestData, setSingleCustomerRequestData] =
    useState(null);
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState("overdue");
  const [sortDirection, setSortDirection] = useState("desc");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedData, setSelectedData] = useState([]);
  const [allSelected, setAllSelected] = useState(false);
  const [deselectedIds, setDeselectedIds] = useState(new Set());
  const [customerMetadata, setCustomerMetadata] = useState(null);
  const { data: branches = [] } = useBranches();
  const { getTemplateNameById } = usePaymentRequestTemplatesContext();
  const {
    profiles: paymentProfiles,
    refresh: refreshPaymentProfiles,
  } = usePaymentProfilesContext();
  const [changeProfileCustomers, setChangeProfileCustomers] = useState(null);
  const tradeAccountDetailsRef = useRef(null);

  const showFileImport = useShowFileImport();
  const requestTemplatesEnabled = useRequestTemplates();
  const showCustomerFacts = useAiFeatures();
  const smartNoteV2Enabled = useSmartNoteV2();
  const homeV2Enabled = useHomeV2();
  // The portal exposes openSmartNote only to AI-enabled users. With the V2 flag on, a row click opens
  // the new account modal instead of the trade-account drawer; otherwise the drawer is unchanged.
  const { openSmartNote } = useOutletContext() ?? {};
  const openAccountFromRow = (account) =>
    smartNoteV2Enabled && openSmartNote
      ? openSmartNote({ id: account.id, name: account.name, accountCode: account.accountCode })
      : setCustomerId(account.id);

  // Redirect to /accounts if the URL references a group that doesn't belong
  // to the current merchant (e.g. after switching merchant via MerchantSwitcher).
  useEffect(() => {
    if (!groupId || groupsLoading) return;
    const groupExists = customerGroups.some((g) => g.id === groupId);
    if (!groupExists) {
      navigate("/accounts", { replace: true });
    }
  }, [groupId, customerGroups, groupsLoading, navigate]);

  // Deep link from elsewhere (e.g. the Smart Note "View trade account" link) opens the
  // drawer for a customer via ?account={id}.
  useEffect(() => {
    const account = searchParams.get("account");
    if (account) setCustomerId(account);
  }, [searchParams]);

  const closeTradeAccount = () => {
    setCustomerId(null);
    if (searchParams.get("account")) {
      searchParams.delete("account");
      setSearchParams(searchParams, { replace: true });
    }
  };

  const restrict = (min, max, x) => {
    return x < min ? min : x > max ? max : x;
  };

  // Memoize customer metadata values to prevent race conditions
  const memoizedCustomerMetadata = useMemo(() => {
    return {
      creditCodes: customerMetadata?.creditCodes || [],
      categories: customerMetadata?.categories || [],
      salespeople: customerMetadata?.salespeople || [],
      customPropertyOnes: customerMetadata?.customPropertyOnes || [],
      customPropertyTwos: customerMetadata?.customPropertyTwos || [],
      customPropertyThrees: customerMetadata?.customPropertyThrees || [],
      branches: branches.map((branch) => branch.name) || [],
      groups: customerGroups.map((group) => ({
        value: group.id,
        label: group.name,
      })),
      paymentProfiles: paymentProfiles.map((profile) => ({
        value: profile.id,
        label: profile.name,
      })),
    };
  }, [
    customerMetadata,
    branches,
    customerGroups,
    paymentProfiles,
  ]);

  // Column definitions (extracted so useTableFilters doesn't depend on filters)
  const columns = useMemo(
    () => [
      {
        title: "Account",
        key: "accountCode",
        id: "accountCode",
        width: "112px",
        render: (x) => x.accountCode,
        sortFunc: (a, b) => a.accountCode.localeCompare(b.accountCode),
        type: "string",
      },
      {
        title: "Name",
        key: "name",
        id: "name",
        width: "250px",
        render: (x) => <span id={"notes-" + x.id}>{x.name}</span>,
        sortFunc: (a, b) => a.name.localeCompare(b.name),
        type: "string",
      },
      {
        title: "Payer type",
        titleTooltip: PAYER_TYPE_TOOLTIP,
        // One filter over the whole vocabulary the column can display. It used to reach only the
        // timing persona, which left a reader looking at an "Active" or "Very late" pill with no
        // way to filter for it; customers-service now resolves the merged vocabulary itself.
        key: "payerTypes",
        exportKey: "timingPersona",
        id: "payerTypes",
        width: "150px",
        hide: !showCustomerFacts,
        render: (x) => (
          <PayerTypeLabel
            activityState={x.activityState}
            timingPersona={x.timingPersona}
            lateBand={x.lateBand}
            detail={x.payerTypeDetail}
          />
        ),
        sortFunc: (a, b) => (a.timingPersona || "").localeCompare(b.timingPersona || ""),
        type: "enum",
        values: PAYER_TYPE_OPTIONS,
      },
      {
        title: "DPD",
        titleTooltip: DAYS_PAST_DUE_TOOLTIP,
        key: "oldestInvoiceDaysPastDue",
        exportKey: "oldestInvoiceDaysPastDue",
        id: "oldestInvoiceDaysPastDue",
        width: "110px",
        hide: !showCustomerFacts,
        render: (x) => <DaysPastDueCell value={x.oldestInvoiceDaysPastDue} />,
        // Nothing overdue ranks below the smallest real DPD in either direction.
        sortFunc: (a, b) => (a.oldestInvoiceDaysPastDue ?? -1) - (b.oldestInvoiceDaysPastDue ?? -1),
        type: "number",
        right: true,
      },
      {
        // "Score" said nothing about what it was a score of, or against whom. The name now carries
        // the frame, so the number is readable before anyone hovers anything.
        title: "Payment score",
        titleTooltip: PAYMENT_SCORE_TOOLTIP,
        key: "paymentPerformanceScore",
        exportKey: "performanceScore",
        id: "paymentPerformanceScore",
        width: "140px",
        hide: !showCustomerFacts,
        hiddenByDefault: true,
        render: (x) => (
          <PerformanceScoreCell
            value={x.performanceScore}
            detail={{
              confidence: x.performanceScoreConfidence,
              daysPastDuePercentile: x.performanceScoreDaysPastDuePercentile,
              onTimePercentile: x.performanceScoreOnTimePercentile,
              effectiveInvoiceCount: x.performanceScoreEffectiveInvoiceCount,
              peerCount: x.performanceScorePeerCount,
            }}
          />
        ),
        // Unscored accounts rank below the lowest real score (0–100) in either direction.
        sortFunc: (a, b) => (a.performanceScore ?? -1) - (b.performanceScore ?? -1),
        type: "number",
        right: true,
      },
      {
        title: "WatchFlags",
        titleTooltip: WATCH_FLAGS_TOOLTIP,
        key: "watchFlags",
        id: "watchFlags",
        width: "440px",
        hide: !showCustomerFacts,
        render: (x) => <WatchFlagChips flags={x.watchFlags} details={x.watchFlagDetails} />,
        type: "enum",
        values: WATCH_FLAG_OPTIONS,
      },
      {
        title: "Groups",
        key: "groups",
        id: "groups",
        width: "250px",
        render: (x) =>
          x.groups?.length > 0 ? <GroupLabels groups={x.groups} /> : null,
        type: "includeExcludeEnum",
        values: memoizedCustomerMetadata.groups,
      },
      {
        title: "Payment profile",
        key: "paymentProfileId",
        id: "paymentProfileId",
        width: "180px",
        render: (x) => (
          <PaymentProfileSelector
            customerId={x.id}
            customerName={x.name}
            paymentProfileId={x.paymentProfileId}
            paymentProfiles={paymentProfiles}
            onChange={(newProfileId) => {
              queryClient.setQueriesData(
                { queryKey: ["customers", merchant?.id] },
                (old) => {
                  if (!old?.data) return old;
                  return {
                    ...old,
                    data: old.data.map((c) =>
                      c.id === x.id
                        ? { ...c, paymentProfileId: newProfileId }
                        : c,
                    ),
                  };
                },
              );
              refreshPaymentProfiles();
            }}
          />
        ),
        type: "enum",
        values: memoizedCustomerMetadata.paymentProfiles,
      },
      {
        title: "Credit code",
        key: "creditCode",
        id: "creditCode",
        width: "120px",
        hide: !merchant?.erpSettings?.showCreditCode,
        render: (x) => x.creditCode ?? "Unknown",
        type: "enum",
        values: memoizedCustomerMetadata.creditCodes,
      },
      {
        title: "All invoices",
        key: "balance",
        id: "balance",
        width: "140px",
        render: (x) => formatAmount(x.balance),
        sortFunc: (a, b) => a.balance - b.balance,
        type: "number",
        filterPrefix: "£",
        right: true,
      },
      {
        title: "Overdue",
        key: "overdue",
        id: "overdue",
        width: "140px",
        render: (x) => (
          <span className="fw-bold">{formatAmount(x.overdue ?? 0)}</span>
        ),
        sortFunc: (a, b) => a.overdue - b.overdue,
        type: "number",
        filterPrefix: "£",
        right: true,
      },
      {
        title: "Due this month",
        key: "dueThisMonth",
        id: "dueThisMonth",
        width: "170px",
        render: (x) => formatAmount(x.dueThisMonth),
        sortFunc: (a, b) => a.dueThisMonth - b.dueThisMonth,
        type: "number",
        filterPrefix: "£",
        right: true,
      },
      {
        title: "Due in future months",
        key: "current",
        id: "current",
        width: "220px",
        render: (x) => formatAmount(x.current),
        sortFunc: (a, b) => a.current - b.current,
        type: "number",
        filterPrefix: "£",
        right: true,
      },
      {
        title: "Last message sent",
        key: "lastMessageTemplate",
        id: "lastMessageTemplate",
        width: "200px",
        render: (x) =>
          x.lastMessageTemplate ??
          (x.lastPaymentRequestTemplateId
            ? (getTemplateNameById(x.lastPaymentRequestTemplateId) ?? null)
            : null),
        type: "enum",
      },
      {
        title: "Credit limit",
        key: "creditLimit",
        id: "creditLimit",
        width: "140px",
        render: (x) => formatAmount(x.creditLimit),
        sortFunc: (a, b) => a.creditLimit - b.creditLimit,
        type: "number",
        filterPrefix: "£",
        right: true,
      },
      {
        title: "Utilisation",
        key: "creditUtilisation",
        id: "creditUtilisation",
        width: "160px",
        render: (x) => (
          <div className="flex items-center gap-3 pl-3">
            <div className="overflow-hidden rounded-full bg-zinc-200 flex-1">
              <div
                style={{ width: `${restrict(0, 100, x.creditUtilisation)}%` }}
                className={`h-0.5 mr-2 rounded-full bg-${
                  x.creditUtilisation < 50
                    ? "emerald-500"
                    : x.creditUtilisation < 75
                      ? "yellow-500"
                      : x.creditUtilisation < 100
                        ? "primary"
                        : "red-500"
                }`}
              />
            </div>
            <p className="m-0">{x.creditUtilisation}%</p>
          </div>
        ),
        type: "number",
        filterSuffix: "%",
        sortFunc: (a, b) => a.creditUtilisation - b.creditUtilisation,
      },
      {
        title: "Last interaction",
        key: "lastEvent",
        id: "lastEvent",
        width: "200px",
        render: (x) => x.lastEvent,
      },
      {
        title: `Due in ${getMonth(-1)}`,
        key: "dueOneMonthAgo",
        id: "dueOneMonthAgo",
        width: "170px",
        render: (x) => formatAmount(x.dueOneMonthAgo),
        sortFunc: (a, b) => a.dueOneMonthAgo - b.dueOneMonthAgo,
        type: "number",
        filterPrefix: "£",
        right: true,
      },
      {
        title: `Due in ${getMonth(-2)}`,
        key: "dueTwoMonthsAgo",
        id: "dueTwoMonthsAgo",
        width: "170px",
        render: (x) => formatAmount(x.dueTwoMonthsAgo),
        sortFunc: (a, b) => a.dueOneMonthAgo - b.dueTwoMonthsAgo,
        type: "number",
        filterPrefix: "£",
        right: true,
      },
      {
        title: `Due before ${getMonth(-2)}`,
        key: "dueThreeOrMoreMonthsAgo",
        id: "dueThreeOrMoreMonthsAgo",
        width: "180px",
        render: (x) => formatAmount(x.dueThreeOrMoreMonthsAgo),
        sortFunc: (a, b) =>
          a.dueThreeOrMoreMonthsAgo - b.dueThreeOrMoreMonthsAgo,
        type: "number",
        filterPrefix: "£",
        right: true,
      },
      {
        title: "Unallocated",
        key: "unallocated",
        id: "unallocated",
        width: "120px",
        render: (x) => formatAmount(x.unallocated),
        sortFunc: (a, b) => a.unallocated - b.unallocated,
        type: "number",
        filterPrefix: "£",
        right: true,
      },
      {
        title: "Last updated",
        key: "lastUpdated",
        id: "lastUpdated",
        width: "140px",
        render: (x) => formatTimeAgo(x.lastEventOccurredOn),
        sortFunc: (a, b) => {
          const dateA = new Date(a.lastEventOccurredOn).getTime() || 0;
          const dateB = new Date(b.lastEventOccurredOn).getTime() || 0;
          return dateA - dateB;
        },
      },
      {
        title: "Financed",
        key: "isFinanced",
        id: "isFinanced",
        width: "120px",
        hide: !merchant?.erpSettings?.showIsFinanced,
        render: (x) => x.isFinanced,
        type: "enum",
        values: ["Yes", "No"],
      },
      {
        title: "Category",
        key: "categoryCode",
        id: "categoryCode",
        width: "120px",
        hide: !merchant?.erpSettings?.showCategory,
        render: (x) => x.categoryCode ?? "-",
        type: "enum",
        values: memoizedCustomerMetadata.categories,
      },
      {
        title: "Total Due",
        key: "totalDue",
        id: "totalDue",
        width: "220px",
        render: (x) => formatAmount(x.totalDue),
        sortFunc: (a, b) => a.totalDue - b.totalDue,
        type: "number",
        filterPrefix: "£",
        right: true,
      },
      {
        title: "Sales Person",
        key: "salesPersonCode",
        id: "salesPersonCode",
        width: "150px",
        hide: !merchant?.erpSettings?.showSalesperson,
        render: (x) => x.salesPersonCode ?? "-",
        sortFunc: (a, b) =>
          (a.salesPersonCode || "").localeCompare(b.salesPersonCode || ""),
        type: "enum",
        values: memoizedCustomerMetadata.salespeople,
      },
      {
        title: "Branch",
        key: "branch",
        id: "branch",
        width: "120px",
        render: (x) => x.branch ?? "-",
        sortFunc: (a, b) => (a.branch || "").localeCompare(b.branch || ""),
        ...(isBranchScoped ? {} : { type: "enum", values: memoizedCustomerMetadata.branches }),
      },
      {
        title:
          merchant?.erpSettings?.customProperty1Name ?? "Custom Property 1",
        key: "customProperty1",
        id: "customProperty1",
        width: "200px",
        hide: !merchant?.erpSettings?.showCustomProperty1,
        render: (x) => x.customProperty1 ?? "-",
        sortFunc: (a, b) =>
          (a.customProperty1 || "").localeCompare(b.customProperty1 || ""),
        type: "enum",
        values: memoizedCustomerMetadata.customPropertyOnes,
      },
      {
        title:
          merchant?.erpSettings?.customProperty2Name ?? "Custom Property 2",
        key: "customProperty2",
        id: "customProperty2",
        width: "200px",
        hide: !merchant?.erpSettings?.showCustomProperty2,
        render: (x) => x.customProperty2 ?? "-",
        sortFunc: (a, b) =>
          (a.customProperty2 || "").localeCompare(b.customProperty2 || ""),
        type: "enum",
        values: memoizedCustomerMetadata.customPropertyTwos,
      },
      {
        title:
          merchant?.erpSettings?.customProperty3Name ?? "Custom Property 3",
        key: "customProperty3",
        id: "customProperty3",
        width: "200px",
        hide: !merchant?.erpSettings?.showCustomProperty3,
        render: (x) => x.customProperty3 ?? "-",
        sortFunc: (a, b) =>
          (a.customProperty3 || "").localeCompare(b.customProperty3 || ""),
        type: "enum",
        values: memoizedCustomerMetadata.customPropertyThrees,
      },
    ],
    [
      merchant,
      memoizedCustomerMetadata,
      paymentProfiles,
      getTemplateNameById,
      isBranchScoped,
      showCustomerFacts,
    ],
  );

  // A working set (?set={id}) is a base layer, not visible filters: its filters are added to every
  // query underneath whatever the user filters on top, and those extra filters are remembered per
  // set. Its own fields can't be stacked on (that would override or widen the set), so they're
  // dropped from the filterable columns while it's open.
  const { sets: workingSets, isLoading: workingSetsLoading } = useWorkingSetList();
  const setId = groupId || !homeV2Enabled ? null : searchParams.get("set");
  const activeWorkingSet = setId ? workingSets.find((s) => s.id === setId) ?? null : null;
  const baseFilters = useMemo(() => activeWorkingSet?.filters ?? [], [activeWorkingSet]);
  const baseKeys = useMemo(() => new Set(baseFilters.map((f) => f.key)), [baseFilters]);

  const filterManager = useTableFilters("tradeAccounts", columns, {
    persistEnabled: true,
    scope: setId ? `set-${setId}` : groupId,
  });
  const { filters: userFilters, setFilters } = filterManager;
  const filters = useMemo(() => withWorkingSet(baseFilters, userFilters), [baseFilters, userFilters]);
  const filterableColumns = useMemo(
    () => (baseKeys.size ? columns.map((c) => (baseKeys.has(c.key) ? { ...c, type: undefined } : c)) : columns),
    [columns, baseKeys],
  );

  const [buildingWorkingSet, setBuildingWorkingSet] = useState(false);
  const [hoveredStep, setHoveredStep] = useState(null);
  const [planCollapsed, togglePlan] = useWorkingSetReadCollapsed();
  const isRowInHoveredStep = useMemo(() => {
    if (!hoveredStep) return undefined;
    const ids = new Set(hoveredStep.customerIds ?? [hoveredStep.customerId]);
    return (row) => ids.has(row.id);
  }, [hoveredStep]);
  const [editingWorkingSet, setEditingWorkingSet] = useState(false);
  const [removingWorkingSet, setRemovingWorkingSet] = useState(false);

  const leaveWorkingSet = useCallback(() => {
    searchParams.delete("set");
    setSearchParams(searchParams, { replace: true });
  }, [searchParams, setSearchParams]);

  // A set that disappears falls back to all accounts. One removed while open already said so; only
  // a stale link (never found this visit) needs telling.
  const seenSetIdRef = useRef(null);
  if (activeWorkingSet) seenSetIdRef.current = activeWorkingSet.id;
  useEffect(() => {
    if (!setId || workingSetsLoading || activeWorkingSet) return;
    if (seenSetIdRef.current !== setId) toast({ description: "That working set no longer exists." });
    leaveWorkingSet();
  }, [setId, workingSetsLoading, activeWorkingSet, leaveWorkingSet]);

  // ?newWorkingSet=1 (from the builder's "build it on Trade accounts"): start from a clean filter bar.
  useEffect(() => {
    if (!homeV2Enabled || !searchParams.get("newWorkingSet")) return;
    setFilters([]);
    setBuildingWorkingSet(true);
    searchParams.delete("newWorkingSet");
    setSearchParams(searchParams, { replace: true });
  }, [searchParams, setSearchParams, setFilters, homeV2Enabled]);

  // Fetch customer metadata and branches on component mount
  useEffect(() => {
    async function fetchCustomerMetadata() {
      try {
        const metadata = await getCustomerMetadata();
        setCustomerMetadata(metadata);
      } catch (error) {
        console.error("Failed to fetch customer metadata:", error);
      }
    }

    fetchCustomerMetadata();
  }, []);

  const getQueryParams = useCallback(() => {
    return {
      page,
      pageSize: 100,
      filters,
      sort,
      sortDirection,
      searchTerm,
      includeGroups: groupId ? [groupId] : [],
    };
  }, [filters, sort, sortDirection, searchTerm, page, groupId]);

  const query = useTableQuery({
    resource: "customers",
    fetcher: fetchCustomersV2,
    getQueryParams,
  });
  const ledgerSync = useLedgerSyncStatus(merchant?.id);

  // Reset to first page when the result-set scope changes.
  useEffect(() => {
    setPage(1);
  }, [filters, sort, sortDirection, searchTerm, groupId]);

  const exportData = async () => {
    const totalRows = query.data?.rows ?? 10000;
    const apiQuery = buildApiQuery({
      page: 1,
      pageSize: totalRows,
      filters,
      sort,
      sortDirection,
      searchTerm,
      includeGroups: groupId ? [groupId] : [],
    });
    const response = await fetchCustomersV2(apiQuery);
    return response.data;
  };

  const importData = async (file) => {
    if (!file) return null;
    return await uploadErpFile(file);
  };

  const onBatchComplete = () => {
    queryClient.invalidateQueries({ queryKey: ["customers"] });
    if (customerId && tradeAccountDetailsRef.current) {
      tradeAccountDetailsRef.current.reloadData();
    }
  };

  const handleOpenSingleRequest = () =>
    requestTemplatesEnabled
      ? setTemplateBatchData(
          query.data?.data?.find((account) => account.id === customerId),
        )
      : setSingleCustomerRequestData(
          query.data?.data?.find((account) => account.id === customerId),
        );

  const handleSort = (key) => {
    if (sort === key) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSort(key);
      setSortDirection("desc");
    }
  };

  const onSelectAllChanged = () => {
    const newAllSelected = !allSelected;
    setAllSelected(newAllSelected);
    setDeselectedIds(new Set()); // Clear deselected items when toggling select all
    setSelectedData(
      newAllSelected
        ? (query.data?.data || []).filter((x) =>
            tableConfig.isSelectable ? tableConfig.isSelectable(x) : true,
          )
        : [],
    );
  };

  const onSelectedChange = (x) => {
    if (allSelected) {
      // In "select all" mode, track deselected items
      setDeselectedIds((prevDeselected) => {
        const newDeselected = new Set(prevDeselected);
        if (newDeselected.has(x.id)) {
          // If it was deselected, remove it from deselected (making it selected again)
          newDeselected.delete(x.id);
        } else {
          // If it was selected, add it to deselected
          newDeselected.add(x.id);
        }
        return newDeselected;
      });
    } else {
      // In individual selection mode, use the normal logic
      setSelectedData((prevSelected) => {
        const isSelected = prevSelected.some((item) => item.id === x.id);

        if (isSelected) {
          // If it is, remove it from the selectedData
          return prevSelected.filter((item) => item.id !== x.id);
        } else {
          // If it isn't, add it to the selectedData
          return [...prevSelected, x];
        }
      });
    }
  };

  const effectiveSelectedCount = allSelected
    ? (query.data?.rows ?? 0) - deselectedIds.size
    : selectedData.length;

  const tableConfig = useMemo(
    () => ({
      key: "tradeAccounts",
      search: (x, s) =>
        x.name.toUpperCase().includes(s.toUpperCase()) ||
        x.accountCode.toUpperCase().includes(s.toUpperCase()),
      onClick: (x) => openAccountFromRow(x),
      isSelectable: (x) => true,
      selectable: true,
      menu: [
        paymentProfiles.length > 1 && {
            label: "Change payment profile",
            icon: CreditCardIcon,
            subtext: `(${effectiveSelectedCount} selected)`,
            disabled: effectiveSelectedCount < 1,
            permission: updateCustomer,
            onClick: async () => {
              let customerData = selectedData;

              if (allSelected) {
                const query = buildApiQuery({
                  page: 1,
                  pageSize: 999999,
                  filters,
                  sort,
                  sortDirection,
                  searchTerm,
                  includeGroups: groupId ? [groupId] : [],
                });
                const response = await getCustomers(query);
                const allData = response.data || [];
                customerData = allData.filter(
                  (item) => !deselectedIds.has(item.id),
                );
              }

              setChangeProfileCustomers(
                customerData.map((c) => ({
                  id: c.id,
                  paymentProfileId: c.paymentProfileId,
                })),
              );
            },
          },
      ].filter(Boolean),
      bulkActions: [
        requestTemplatesEnabled
          ? {
              label: "Request payments",
              primary: true,
              icon: PaperAirplaneIcon,
              action: async (selectedItems) => {
                let customerData = selectedItems;

                if (allSelected) {
                  const query = buildApiQuery({
                    page: 1,
                    pageSize: 999999,
                    filters,
                    sort,
                    sortDirection,
                    searchTerm,
                    includeGroups: groupId ? [groupId] : [],
                  });
                  const response = await getCustomers(query);
                  const allData = response.data || [];
                  customerData = allData.filter(
                    (item) => !deselectedIds.has(item.id),
                  );
                }

                setTemplateBatchData(customerData);
              },
              permission: updateCustomer,
            }
          : {
              label: "Request payments",
              primary: true,
              icon: PaperAirplaneIcon,
              action: async (selectedItems) => {
                let customerData = selectedItems;

                // If allSelected is true, we need to fetch all data from the API
                if (allSelected) {
                  const query = buildApiQuery({
                    page: 1,
                    pageSize: 999999, // Large number to get all records
                    filters,
                    sort,
                    sortDirection,
                    searchTerm,
                    includeGroups: groupId ? [groupId] : [],
                  });
                  const response = await getCustomers(query);
                  const allData = response.data || [];
                  // Exclude deselected items
                  customerData = allData.filter(
                    (item) => !deselectedIds.has(item.id),
                  );
                }

                // Handle the bulk action based on data length
                if (customerData.length > 1) {
                  setBatchCustomerRequestData(customerData);
                } else {
                  setSingleCustomerRequestData(customerData[0]);
                }
              },
              permission: updateCustomer,
            },
      ].filter(Boolean),
      rowActions: [
        {
          permission: updateCustomer,
          onClick: requestTemplatesEnabled
            ? setTemplateBatchData
            : setSingleCustomerRequestData,
          icon: PaperAirplaneIcon,
          title: "Request payment",
        },
      ],
      colCustomisation: true,
      columns: filterableColumns,
    }),
    [
      filterableColumns,
      columns,
      updateCustomer,
      filters,
      sort,
      sortDirection,
      searchTerm,
      allSelected,
      deselectedIds,
      groupId,
      paymentProfiles,
      effectiveSelectedCount,
      selectedData,
      showFileImport,
      requestTemplatesEnabled,
      smartNoteV2Enabled,
      openSmartNote,
    ],
  );

  const statistics = [
    {
      label: "All",
      value: query.data ? query.data.statistics.all : null,
    },
    {
      label: "All invoices",
      value: formatAmount(
        query.data ? query.data.statistics.allInvoices : null,
      ),
    },
    {
      label: "All overdue",
      value: formatAmount(
        query.data ? query.data.statistics.allOverdue : null,
      ),
    },
    {
      label: "Due this month",
      value: formatAmount(
        query.data ? query.data.statistics.dueThisMonth : null,
      ),
    },
    {
      label: "Due in future months",
      value: formatAmount(
        query.data ? query.data.statistics.dueInFutureMonths : null,
      ),
    },
  ];

  return (
    <>
      <PageHeader
        actions={
          activeWorkingSet && showCustomerFacts && <WorkingSetReadChip open={!planCollapsed} onToggle={togglePlan} />
        }
      >
        {activeWorkingSet ? (
          <WorkingSetTitle
            set={activeWorkingSet}
            onEdit={() => setEditingWorkingSet(true)}
            onRemove={() => setRemovingWorkingSet(true)}
            onExit={leaveWorkingSet}
          />
        ) : (
          <PageTitle>
            {groupId ? getGroupName(groupId) || "All accounts" : setId ? "\u00a0" : "All accounts"}
          </PageTitle>
        )}
      </PageHeader>
      {activeWorkingSet && showCustomerFacts && (
        <WorkingSetRead
          key={activeWorkingSet.id}
          set={activeWorkingSet}
          accountCount={query.data ? query.data.statistics.all : null}
          collapsed={planCollapsed}
          onToggle={togglePlan}
          onHoverStep={setHoveredStep}
        />
      )}
      <ServerTable
        config={tableConfig}
        data={query.data?.data || []}
        loading={query.isLoading || query.isPlaceholderData}
        statistics={statistics}
        isRowHighlighted={isRowInHoveredStep}
        toolbarLeft={
          ledgerSync.data?.lastSyncedAt && (
            <span className="text-xs text-zinc-500">
              Ledger last synced{" "}
              {formatDateTime(ledgerSync.data.lastSyncedAt, " at ")}
            </span>
          )
        }
        headerAction={
          buildingWorkingSet && !activeWorkingSet && (
            <WorkingSetBar
              filters={userFilters}
              onCreated={(created) => {
                setBuildingWorkingSet(false);
                navigate(`/accounts?set=${created.id}`);
              }}
              onExit={() => setBuildingWorkingSet(false)}
            />
          )
        }
        results={query.data?.rows || 0}
        page={page}
        setPage={setPage}
        pageSize={100}
        filterManager={filterManager}
        sort={sort}
        sortDirection={sortDirection}
        onSort={handleSort}
        searchTerm={searchTerm}
        exportData={exportData}
        importData={showFileImport ? importData : null}
        onSearchTermChange={(e) => setSearchTerm(e.target.value)}
        selectedData={selectedData}
        setSelectedData={setSelectedData}
        allSelected={allSelected}
        deselectedIds={deselectedIds}
        onSelectAllChanged={onSelectAllChanged}
        onSelectedChange={onSelectedChange}
        selectedItemLabel={(count) =>
          `account${count === 1 ? "" : "s"} selected`
        }
      />
      <BatchRequestDrawer
        isOpen={!!batchCustomerRequestData}
        customerData={batchCustomerRequestData}
        onClose={() => setBatchCustomerRequestData(null)}
        onComplete={onBatchComplete}
      />
      <BatchTemplateRequestModal
        isOpen={!!templateBatchData}
        customerData={templateBatchData}
        onClose={() => setTemplateBatchData(null)}
        onComplete={onBatchComplete}
      />
      <SingleRequestDrawer
        isOpen={!!singleCustomerRequestData}
        customer={singleCustomerRequestData}
        onClose={() => setSingleCustomerRequestData(null)}
        onComplete={() => {
          queryClient.invalidateQueries({ queryKey: ["customers"] });
          if (customerId && tradeAccountDetailsRef.current) {
            tradeAccountDetailsRef.current.reloadData();
          }
        }}
      />
      <TradeAccountDetails
        ref={tradeAccountDetailsRef}
        isOpen={!!customerId}
        customerId={customerId}
        onChange={() =>
          queryClient.invalidateQueries({ queryKey: ["customers"] })
        }
        onClose={closeTradeAccount}
        openSingleRequest={handleOpenSingleRequest}
      />
      <ChangePaymentProfileModal
        isOpen={!!changeProfileCustomers}
        customers={changeProfileCustomers}
        paymentProfiles={paymentProfiles}
        onClose={() => setChangeProfileCustomers(null)}
        onComplete={() => {
          queryClient.invalidateQueries({ queryKey: ["customers"] });
          refreshPaymentProfiles();
        }}
      />
      <WorkingSetBuilderModal
        isOpen={editingWorkingSet && !!activeWorkingSet}
        set={activeWorkingSet}
        onClose={() => setEditingWorkingSet(false)}
        onRemoved={leaveWorkingSet}
      />
      <RemoveWorkingSetDialog
        set={removingWorkingSet ? activeWorkingSet : null}
        onClose={() => setRemovingWorkingSet(false)}
        onRemoved={leaveWorkingSet}
      />
    </>
  );
};

export default Accounts;
